import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Stethoscope, Video } from "lucide-react";
import Navbar from "../components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

function fmt(iso) {
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
}

const STATUS_BADGES = {
  upcoming: "bg-accent text-accent-foreground",
  in_progress: "bg-blue-50 text-blue-700 font-semibold",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

export default function ClinicAppointments() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clinic/me/appointments?status=${tab}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main data-testid="clinic-appts-page" className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-6xl w-full mx-auto space-y-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Clinic appointments</h1>
          <p className="text-sm text-muted-foreground mt-1">Today / Upcoming / Completed / Cancelled.</p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-secondary p-1 rounded-full h-12 flex-wrap">
            {["today", "upcoming", "completed", "cancelled"].map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                data-testid={`clinic-appts-tab-${t}`}
                className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm capitalize"
              >
                {t}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={tab} className="mt-6 space-y-3">
            {loading ? (
              [1, 2].map((i) => <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />)
            ) : items.length === 0 ? (
              <div data-testid="clinic-appts-empty" className="bg-white border border-border rounded-2xl p-10 text-center">
                <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <div className="font-semibold text-lg">No {tab} appointments</div>
              </div>
            ) : (
              items.map((a) => (
                <div
                  key={a.id}
                  data-testid={`clinic-appt-card-${a.id}`}
                  onClick={() => navigate(`/clinic/appointments/${a.id}/checkups`)}
                  className="bg-white border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{a.patient?.name}</span>
                      {a.walkin && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">walk-in</span>}
                      <span className={"text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " + (STATUS_BADGES[a.status] || "bg-muted text-muted-foreground")}>
                        {a.status}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground inline-flex items-center gap-1">
                        {a.mode === "video" ? <Video className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                        {a.mode}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{a.doctor?.name} · {a.doctor?.category}</div>
                    <div className="text-sm mt-1 inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-muted-foreground" />{fmt(a.scheduled_at)}</div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {a.mode === "video" && a.status !== "completed" && (
                      <Button
                        data-testid={`clinic-appt-join-${a.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clinic/consult/${a.id}`);
                        }}
                        variant="outline"
                        className="rounded-full border-primary text-primary hover:bg-primary/10 gap-1.5"
                      >
                        <Video className="h-3.5 w-3.5" />
                        Join Call
                      </Button>
                    )}
                    {a.status === "completed" ? (
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clinic/appointments/${a.id}/checkups`);
                        }}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >
                        Completed · Checkups
                      </Button>
                    ) : (
                      <Button
                        data-testid={`clinic-appt-checkups-${a.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clinic/appointments/${a.id}/checkups`);
                        }}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                      >
                        Checkups
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
